import React, { useRef, useEffect, useState } from 'react'
import { Box, Flex } from '@workwhile/ui'
import { User } from 'api/typings'
import {
  AdminUserPaymentProfileResponse,
  UserOutstandingBalance,
} from 'api/payments'
import { UserBox } from './UserBox'
import { StripeBox } from './StripeBox'
import { MarqetaBox } from './MarqetaBox'
import { EvereeBox } from './EvereeBox'
import { BBSIBox } from './BBSIBox'
import { HighnoteBox } from './HighnoteBox'

interface Props {
  user: User
  outstandingBalance: UserOutstandingBalance
  paymentProfile: AdminUserPaymentProfileResponse
}

export const MoneyFlowVisualization = ({
  user,
  outstandingBalance,
  paymentProfile,
}: Props) => {
  const userBoxRef = useRef<HTMLDivElement>(null)
  const stripeBoxRef = useRef<HTMLDivElement>(null)
  const marqetaBoxRef = useRef<HTMLDivElement>(null)
  const evereeBoxRef = useRef<HTMLDivElement>(null)
  const bbsiBoxRef = useRef<HTMLDivElement>(null)
  const highnoteBoxRef = useRef<HTMLDivElement>(null)

  const [arrowPaths, setArrowPaths] = useState({
    toStripe: '',
    toMarqeta: '',
    toEveree: '',
    toBBSI: '',
    toHighnote: '',
  })

  // Calculate arrow paths based on box positions
  useEffect(() => {
    const updateArrows = () => {
      const container = userBoxRef.current?.parentElement
      if (!container) return

      const containerRect = container.getBoundingClientRect()

      // Calculate main arrows (user box to provider boxes)
      let pathToStripe = ''
      let pathToMarqeta = ''
      let pathToEveree = ''
      let pathToBBSI = ''
      let pathToHighnote = ''

      if (
        userBoxRef.current &&
        evereeBoxRef.current &&
        window.innerWidth >= 768
      ) {
        const userRect = userBoxRef.current.getBoundingClientRect()
        const evereeRect = evereeBoxRef.current.getBoundingClientRect()
        const stripeRect = stripeBoxRef.current?.getBoundingClientRect()
        const marqetaRect = marqetaBoxRef.current?.getBoundingClientRect()
        const bbsiRect = bbsiBoxRef.current?.getBoundingClientRect()

        // Calculate relative positions
        const userRight = userRect.right - containerRect.left
        const userCenterY =
          userRect.top + userRect.height / 2 - containerRect.top

        const evereeLeft = evereeRect.left - containerRect.left
        const evereeCenterY =
          evereeRect.top + evereeRect.height / 2 - containerRect.top

        // Midpoint for branching - all arrows start from center of user box
        const branchX = userRight + 30

        // Path to Stripe (only if Stripe box exists)
        if (stripeRect) {
          const stripeLeft = stripeRect.left - containerRect.left
          const stripeCenterY =
            stripeRect.top + stripeRect.height / 2 - containerRect.top
          pathToStripe = `M ${userRight} ${userCenterY} L ${branchX} ${userCenterY} L ${branchX} ${stripeCenterY} L ${stripeLeft} ${stripeCenterY}`
        }

        // Path to Marqeta (only if Marqeta box exists)
        if (marqetaRect) {
          const marqetaLeft = marqetaRect.left - containerRect.left
          const marqetaCenterY =
            marqetaRect.top + marqetaRect.height / 2 - containerRect.top
          pathToMarqeta = `M ${userRight} ${userCenterY} L ${branchX} ${userCenterY} L ${branchX} ${marqetaCenterY} L ${marqetaLeft} ${marqetaCenterY}`
        }

        // Path to Everee: from center, horizontal, then to Everee
        pathToEveree = `M ${userRight} ${userCenterY} L ${branchX} ${userCenterY} L ${branchX} ${evereeCenterY} L ${evereeLeft} ${evereeCenterY}`

        // Path to BBSI: from center, horizontal, then to BBSI (only if BBSI box exists)
        if (bbsiRect) {
          const bbsiLeft = bbsiRect.left - containerRect.left
          const bbsiCenterY =
            bbsiRect.top + bbsiRect.height / 2 - containerRect.top
          pathToBBSI = `M ${userRight} ${userCenterY} L ${branchX} ${userCenterY} L ${branchX} ${bbsiCenterY} L ${bbsiLeft} ${bbsiCenterY}`
        }

        // Path to Highnote: from center, horizontal, then to Highnote (only if Highnote box exists)
        const highnoteRect = highnoteBoxRef.current?.getBoundingClientRect()
        if (highnoteRect) {
          const highnoteLeft = highnoteRect.left - containerRect.left
          const highnoteCenterY =
            highnoteRect.top + highnoteRect.height / 2 - containerRect.top
          pathToHighnote = `M ${userRight} ${userCenterY} L ${branchX} ${userCenterY} L ${branchX} ${highnoteCenterY} L ${highnoteLeft} ${highnoteCenterY}`
        }
      }

      setArrowPaths({
        toStripe: pathToStripe,
        toMarqeta: pathToMarqeta,
        toEveree: pathToEveree,
        toBBSI: pathToBBSI,
        toHighnote: pathToHighnote,
      })
    }

    // Use requestAnimationFrame to ensure DOM is fully laid out
    const timeoutId = setTimeout(() => {
      updateArrows()
    }, 100)

    window.addEventListener('resize', updateArrows)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', updateArrows)
    }
  }, [outstandingBalance, paymentProfile])

  return (
    <Box my={4}>
      <Box position="relative" minHeight="400px">
        {/* SVG Overlay for arrows */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1,
            overflow: 'visible',
          }}
          className="money-flow-arrows"
        >
          <defs>
            <marker
              id="arrowhead-primary"
              markerWidth="6"
              markerHeight="6"
              refX="5"
              refY="2"
              orient="auto"
            >
              <polygon points="0 0, 6 2, 0 4" fill="#27C8B6" />
            </marker>
          </defs>
          {arrowPaths.toStripe ? (
            <path
              d={arrowPaths.toStripe}
              stroke="#27C8B6"
              strokeWidth="1.5"
              fill="none"
              markerEnd="url(#arrowhead-primary)"
            />
          ) : null}
          {arrowPaths.toMarqeta ? (
            <path
              d={arrowPaths.toMarqeta}
              stroke="#27C8B6"
              strokeWidth="1.5"
              fill="none"
              markerEnd="url(#arrowhead-primary)"
            />
          ) : null}
          {arrowPaths.toEveree ? (
            <path
              d={arrowPaths.toEveree}
              stroke="#27C8B6"
              strokeWidth="1.5"
              fill="none"
              markerEnd="url(#arrowhead-primary)"
            />
          ) : null}
          {arrowPaths.toBBSI ? (
            <path
              d={arrowPaths.toBBSI}
              stroke="#27C8B6"
              strokeWidth="1.5"
              fill="none"
              markerEnd="url(#arrowhead-primary)"
            />
          ) : null}
          {arrowPaths.toHighnote ? (
            <path
              d={arrowPaths.toHighnote}
              stroke="#27C8B6"
              strokeWidth="1.5"
              fill="none"
              markerEnd="url(#arrowhead-primary)"
            />
          ) : null}
        </svg>

        <Flex
          flexDirection={['column', 'column', 'row']}
          alignItems={['center', 'center', 'flex-start']}
          justifyContent={['center', 'center', 'flex-start']}
          gap={6}
          position="relative"
          zIndex={2}
          pl={[0, 0, 4]}
          maxWidth="100%"
          flexWrap="wrap"
        >
          {/* User Box */}
          <UserBox
            user={user}
            outstandingBalance={outstandingBalance}
            userBoxRef={userBoxRef}
          />

          {/* Right side container for Stripe, Marqeta, and Everee boxes */}
          <Flex
            flexDirection="column"
            gap={4}
            alignItems={['center', 'center', 'flex-start']}
            ml={[0, 0, 4]}
            mt={[4, 4, 0]}
            flex={1}
            minWidth={0}
            maxWidth="100%"
          >
            {/* Stripe Box */}
            <StripeBox
              user={user}
              outstandingBalance={outstandingBalance}
              stripeProfile={paymentProfile.stripeProfile}
              current1099PaymentProvider={
                paymentProfile.current1099PaymentProvider
              }
              isTransitioningFromStripeToHighnote={
                paymentProfile.isTransitioningFromStripeToHighnote ?? false
              }
              stripeBoxRef={stripeBoxRef}
            />
            <MarqetaBox
              user={user}
              outstandingBalance={outstandingBalance}
              marqetaProfile={paymentProfile.marqetaProfile}
              current1099PaymentProvider={
                paymentProfile.current1099PaymentProvider
              }
              marqetaBoxRef={marqetaBoxRef}
            />

            {/* Highnote Box (alternative 1099 provider) */}
            <HighnoteBox
              user={user}
              current1099PaymentProvider={
                paymentProfile.current1099PaymentProvider
              }
              outstandingBalance={outstandingBalance}
              highnoteBoxRef={highnoteBoxRef}
            />

            {/* Everee Box */}
            <EvereeBox
              user={user}
              w2PayrollFrequency={paymentProfile.w2PayrollFrequency}
              w2GarnishmentsNeeded={paymentProfile.w2GarnishmentsNeeded}
              evereeProfile={paymentProfile.evereeProfile}
              outstandingBalance={outstandingBalance}
              evereeBoxRef={evereeBoxRef}
            />
            {/* BBSI Box */}
            <BBSIBox
              outstandingBalance={outstandingBalance}
              bbsiBoxRef={bbsiBoxRef}
            />
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}
